import { supabase } from "./supabase";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    public readonly body: string,
  ) {
    super(`HTTP [${path}] ${status}: ${body}`);
    this.name = "ApiError";
  }
}

export async function authorizedFetch(
  path: string,
  options?: RequestInit,
): Promise<Response> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw new ApiError(401, path, `session lookup failed: ${error.message}`);
  }
  const token = data.session?.access_token;
  if (!token) {
    throw new ApiError(401, path, "no active session");
  }

  const headers = new Headers(options?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Content-Type") && options?.body) {
    headers.set("Content-Type", "application/json");
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, path, body);
  }

  return res;
}

export async function apiJson<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await authorizedFetch(path, options);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function publicApiJson<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const headers = new Headers(options?.headers);
  if (!headers.has("Content-Type") && options?.body) {
    headers.set("Content-Type", "application/json");
  }
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, path, body);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function getStatusPing() {
  const res = await fetch(`${API_BASE}/healthz`);
  return res.json();
}

export async function delayForTesting(t: number) {
  await new Promise((res) => setTimeout(res, t));
}
